import * as React from "react";
import { Box, createStyles, makeStyles } from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { find, includes, map, max, min, uniqBy } from "lodash";
import moment from "moment";
import {
  Legend,
  LegendPayload,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  SegmentItem,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { AppContext } from "contexts";
import { formatCurrencyAmount } from "lib/format";
import { BuyNowPriceHistory, ListingState } from "services/listings/types";
import {
  LIVE_OFFER_STATES,
  NON_FAILED_DEAL_STATES,
} from "services/offers/constants";
import { ListingOffer } from "services/offers/types";
import theme from "theme";
import { Theme } from "theme";

import OfferChartCustomTooltip from "./OfferChartCustomTooltip";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      backgroundColor: theme.custom.palette.gray.light,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[3],
      padding: theme.spacing(2),
      "& li": {
        padding: "unset", // this prevents legend items from overflowing the container
      },
    },
    legendItem: {
      color: theme.palette.text.primary,
      textTransform: "capitalize",
      fontSize: "0.65em",
      padding: "unset",
    },
    referenceLineLabel: {
      [theme.breakpoints.down("xs")]: {
        fontSize: "0.8125rem",
      },
    },
    toggleButtonGroup: {
      "& button": {
        border: `1px solid ${theme.palette.primary.main}`,
        boxShadow: theme.shadows[1],
        color: theme.palette.primary.main,
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: 1,
        padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
        textTransform: "none",
        "&:hover": {
          backgroundColor: theme.custom.palette.blue.lightest,
        },
        "&:not(:first-child)": {
          borderRadius: theme.shape.borderRadius,
          borderLeft: `1px solid ${theme.palette.primary.main}`,
          marginLeft: theme.spacing(1),
        },
        "&:first-child": {
          borderRadius: theme.shape.borderRadius,
        },
      },
      "& .Mui-selected": {
        border: `1px solid ${theme.custom.palette.blue.main}`,
        boxShadow: "none",
        backgroundColor: theme.custom.palette.blue.lightest,
        color: theme.custom.palette.blue.main,
        cursor: "default",
        "&:hover": {
          backgroundColor: theme.custom.palette.blue.lightest,
        },
        "&:not(:first-child)": {
          border: `1px solid ${theme.custom.palette.blue.main}`,
        },
      },
    },
  }),
);

interface OfferChartProps {
  offers: ListingOffer[];
  marketplaceMedian: number;
  buyNowPrice: string;
  buyNowPriceHistory: BuyNowPriceHistory[];
  listingPublishedDate: string | null;
  hasDeal: boolean;
  acceptingFinalOffersStartAt: string | null;
  listingState: ListingState;
}

export interface DataItem {
  date: number;
  amount: number;
  status: string;
  color: string;
}

interface ScatterData {
  data: DataItem[];
  color: string;
}

function sortOffers(offers: ListingOffer[]) {
  const validOffers: { [key: string]: ScatterData } = {
    expired: {
      data: [],
      color: theme.custom.palette.black.main,
    },
    rejected: {
      data: [],
      color: theme.custom.palette.red.main,
    },
    modified: {
      data: [],
      color: theme.custom.palette.yellow.main,
    },
    active: {
      data: [],
      color: theme.custom.palette.blue.main,
    },
    accepted: {
      data: [],
      color: theme.custom.palette.green.main,
    },
  };
  for (const offer of offers) {
    const offerDate = new Date(offer.created_at).getTime();
    const offerAmount = Number(offer.amount);

    if (["fail_offer_timeout"].includes(offer.state)) {
      validOffers["expired"].data.push({
        amount: offerAmount,
        date: offerDate,
        status: "Expired",
        color: validOffers["expired"].color,
      });
    } else if (
      [
        "fail_offer_rejected",
        "fail_offer_auto_rejected",
        "fail_counteroffer_made",
        "fail_docs_timeout",
        "fail_due_diligence",
        "fail_due_diligence_timeout",
        "fail_payment",
        "fail_payment_timeout",
        "fail_transfer",
      ].includes(offer.state)
    ) {
      validOffers["rejected"].data.push({
        amount: offerAmount,
        date: offerDate,
        status: "Rejected",
        color: validOffers["rejected"].color,
      });
    } else if (["fail_buy_order_canceled"].includes(offer.state)) {
      validOffers["modified"].data.push({
        amount: offerAmount,
        date: offerDate,
        status: "Withdrawn",
        color: validOffers["modified"].color,
      });
    } else if (offer.state === "fail_offer_increased") {
      validOffers["modified"].data.push({
        amount: offerAmount,
        date: offerDate,
        status: "Increased",
        color: validOffers["modified"].color,
      });
    } else if (LIVE_OFFER_STATES.includes(offer.state)) {
      validOffers["active"].data.push({
        amount: offerAmount,
        date: offerDate,
        status: "Active",
        color: validOffers["active"].color,
      });
    } else if (includes(NON_FAILED_DEAL_STATES, offer.state)) {
      validOffers["accepted"].data.push({
        amount: offerAmount,
        date: offerDate,
        status: "Accepted",
        color: validOffers["accepted"].color,
      });
    } else if (offer.state === "fail_offer_outbid") {
      validOffers["expired"].data.push({
        amount: offerAmount,
        date: offerDate,
        status: "Outbid",
        color: validOffers["expired"].color,
      });
    }
  }
  return validOffers;
}

function getMaxValue(
  offers: ListingOffer[],
  marketplaceMedian: number,
  buyNowPriceHistory: BuyNowPriceHistory[],
) {
  let maxOffer = marketplaceMedian;
  for (const offer of offers) {
    const offerAmount = Number(offer.amount);
    if (offerAmount > maxOffer) {
      maxOffer = offerAmount;
    }
  }
  for (const buyNowPrice of buyNowPriceHistory) {
    const buyNowPriceAmount = Number(buyNowPrice.buy_now_price);
    if (buyNowPriceAmount > maxOffer) {
      maxOffer = buyNowPriceAmount;
    }
  }
  return maxOffer;
}

function getRecentOffers(offers: ListingOffer[], minDate: moment.Moment) {
  const recentOffers = [] as ListingOffer[];
  for (const offer of offers) {
    if (moment.utc(offer.created_at).isSameOrAfter(minDate)) {
      recentOffers.push(offer);
    }
  }
  return recentOffers;
}

function getRecentBuyNowPriceHistories(
  buyNowPriceHistory: BuyNowPriceHistory[],
  minDate: moment.Moment,
) {
  let historiesInRange = buyNowPriceHistory.filter(entry =>
    moment.utc(entry.created_at).isSameOrAfter(minDate),
  );
  const historiesBeforeRange = buyNowPriceHistory.filter(entry =>
    moment.utc(entry.created_at).isBefore(minDate),
  );
  if (
    !!historiesBeforeRange.length &&
    historiesBeforeRange[historiesBeforeRange.length - 1].buy_now_price !== "-1"
  ) {
    historiesInRange = [
      historiesBeforeRange[historiesBeforeRange.length - 1],
      ...historiesInRange,
    ];
  }

  return historiesInRange;
}

function getBuyNowPriceHistorySegments(
  buyNowPriceHistory: BuyNowPriceHistory[],
  minDate: moment.Moment,
) {
  const buyNowPriceData: LineSegmentType[] = [];
  let previousBuyNowPrice: BuyNowPriceHistory;
  buyNowPriceHistory.forEach((currentBuyNowPrice, index) => {
    const currentBuyNowPriceAmount = Number(currentBuyNowPrice.buy_now_price);
    const previousBuyNowPriceAmount = Number(
      previousBuyNowPrice?.buy_now_price,
    );
    if (!previousBuyNowPrice && currentBuyNowPriceAmount !== -1) {
      previousBuyNowPrice = currentBuyNowPrice;
    } else if (
      previousBuyNowPrice &&
      (previousBuyNowPriceAmount !== currentBuyNowPriceAmount ||
        index === buyNowPriceHistory.length - 1)
    ) {
      let previousBuyNowPriceDate = new Date(
        previousBuyNowPrice.created_at,
      ).getTime();
      if (
        moment.utc(previousBuyNowPrice.created_at).isBefore(minDate) &&
        moment.utc(currentBuyNowPrice.created_at).isAfter(minDate)
      ) {
        previousBuyNowPriceDate = minDate.toDate().getTime();
      }
      buyNowPriceData.push([
        {
          x: previousBuyNowPriceDate,
          y: previousBuyNowPriceAmount,
        },
        {
          x: new Date(currentBuyNowPrice.created_at).getTime(),
          y: previousBuyNowPriceAmount,
        },
      ]);
      if (currentBuyNowPriceAmount !== -1) {
        buyNowPriceData.push([
          {
            x: new Date(currentBuyNowPrice.created_at).getTime(),
            y: previousBuyNowPriceAmount,
          },
          {
            x: new Date(currentBuyNowPrice.created_at).getTime(),
            y: currentBuyNowPriceAmount,
          },
        ]);
        previousBuyNowPrice = currentBuyNowPrice;
      } else {
        previousBuyNowPrice = undefined;
      }
    }
  });
  return buyNowPriceData;
}

interface Range {
  label: string;
  duration: number;
  unit: "days" | "months" | "years";
}

type LineSegmentType = [SegmentItem, SegmentItem];

interface LegendPayloadWithLine extends LegendPayload {
  payload?: Record<"strokeDasharray", string>;
}

const initialRangeOptions: Range[] = [
  { label: "30D", duration: 30, unit: "days" },
  { label: "3M", duration: 3, unit: "months" },
  { label: "6M", duration: 6, unit: "months" },
  { label: "1Y", duration: 1, unit: "years" },
];

const MIN_DAY_RANGE = 30;

const OfferChart: React.FC<OfferChartProps> = ({
  offers,
  marketplaceMedian,
  buyNowPrice,
  buyNowPriceHistory,
  listingPublishedDate,
  hasDeal,
  acceptingFinalOffersStartAt,
  listingState,
}) => {
  const classes = useStyles(theme);
  const { user } = React.useContext(AppContext);
  const canSeeMarketplaceMedian = !!user;
  const isAcceptingFinalOffers = listingState === "accepting_final_offers";
  const isListingOpen = listingState === "open";
  const isAcceptingOffers = isListingOpen || isAcceptingFinalOffers;
  const [rangeOptions, setRangeOptions] = React.useState<Range[]>(
    initialRangeOptions,
  );
  const [range, setRange] = React.useState<Range | null>(null);

  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  const chartDataLastUpdatedAt = React.useMemo(() => moment.utc(), [
    listingState,
    offers.length,
  ]);

  const daysSincePublished = chartDataLastUpdatedAt.diff(
    listingPublishedDate,
    "days",
  );

  React.useEffect(() => {
    const minDayRange = min([daysSincePublished, MIN_DAY_RANGE]);
    const allTimeDaysDuration = max([daysSincePublished, minDayRange]);

    const filteredRangeOptions = initialRangeOptions.filter(
      option =>
        chartDataLastUpdatedAt.diff(listingPublishedDate, option.unit) >=
        option.duration,
    );
    hasDeal
      ? setRangeOptions([
          {
            label: "Max",
            duration: allTimeDaysDuration,
            unit: "days",
          },
        ])
      : !!isAcceptingFinalOffers
      ? setRangeOptions([
          { label: "1D", duration: 1, unit: "days" },
          ...filteredRangeOptions,
          { label: "Max", duration: allTimeDaysDuration, unit: "days" },
        ])
      : setRangeOptions([
          ...filteredRangeOptions,
          { label: "Max", duration: allTimeDaysDuration, unit: "days" },
        ]);
  }, [
    listingPublishedDate,
    hasDeal,
    isAcceptingFinalOffers,
    daysSincePublished,
    chartDataLastUpdatedAt,
  ]);

  React.useEffect(() => {
    setRange(rangeOptions[0]);
  }, [rangeOptions]);

  if (!range) {
    return null;
  }

  const minDate =
    range.duration === daysSincePublished
      ? moment.utc(listingPublishedDate)
      : chartDataLastUpdatedAt.clone().subtract(range.duration, range.unit);

  const acceptedOffer = find(offers, offer => {
    return includes(NON_FAILED_DEAL_STATES, offer.state);
  });

  const xAxisTicks = uniqBy(
    [
      minDate.valueOf(),
      isAcceptingOffers
        ? chartDataLastUpdatedAt.valueOf()
        : moment.utc(acceptedOffer?.created_at).valueOf(),
    ],
    value => moment(value).format("M/D/YY"),
  );

  if (buyNowPrice) {
    buyNowPriceHistory = [
      ...buyNowPriceHistory,
      {
        created_at: isAcceptingOffers
          ? chartDataLastUpdatedAt.toISOString()
          : acceptedOffer?.created_at,
        buy_now_price: buyNowPrice,
      },
    ];
  }
  const recentOffers = getRecentOffers(offers, minDate);
  const chartOffersSorted = sortOffers(recentOffers);
  const recentBuyNowPriceHistoriesForMaxOffer = getRecentBuyNowPriceHistories(
    buyNowPriceHistory,
    minDate,
  );
  const maxOffer = getMaxValue(
    recentOffers,
    marketplaceMedian,
    recentBuyNowPriceHistoriesForMaxOffer,
  );

  const handleRangeChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: string,
  ) => {
    value && setRange(JSON.parse(value) as Range);
  };

  const buyNowPriceHistorySegments: LineSegmentType[] = getBuyNowPriceHistorySegments(
    buyNowPriceHistory,
    minDate,
  );

  const legendPayload: LegendPayloadWithLine[] = [
    {
      id: "listPrice",
      value: "List Price",
      type: "plainline",
      payload: { strokeDasharray: "1 0" },
      color: "black",
    },
    {
      id: "expired",
      value: "Expired",
      type: "circle",
      color: chartOffersSorted["expired"].color,
    },
    {
      id: "rejected",
      value: "Rejected",
      type: "circle",
      color: chartOffersSorted["rejected"].color,
    },
    {
      id: "modified",
      value: "Modified",
      type: "circle",
      color: chartOffersSorted["modified"].color,
    },
    {
      id: "active",
      value: "Active",
      type: "circle",
      color: chartOffersSorted["active"].color,
    },
    {
      id: "accepted",
      value: "Accepted",
      type: "circle",
      color: chartOffersSorted["accepted"].color,
    },
  ];

  return (
    <Box className={classes.container}>
      <Box display="flex" justifyContent="space-between" flexWrap="wrap">
        <Box display="flex" justifyContent="flex-end" flexGrow="2">
          <ToggleButtonGroup
            className={classes.toggleButtonGroup}
            exclusive
            onChange={handleRangeChange}
            value={JSON.stringify(range)}
          >
            {rangeOptions.map(option => (
              <ToggleButton key={option.label} value={JSON.stringify(option)}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>
      <ResponsiveContainer height={400}>
        <ScatterChart margin={{ left: -30, top: 10 }}>
          <Legend
            payload={legendPayload}
            iconSize={10}
            formatter={value => (
              <span className={classes.legendItem}>{value}</span>
            )}
            align="right"
            wrapperStyle={{ right: "-8px" }} // align with the right of the chart
          />
          <XAxis
            dataKey="date"
            type="number"
            domain={[
              minDate.valueOf(),
              isAcceptingOffers
                ? chartDataLastUpdatedAt.valueOf()
                : moment.utc(acceptedOffer?.created_at).valueOf(),
            ]}
            tickCount={xAxisTicks.length}
            tickLine={false}
            tickMargin={10}
            ticks={xAxisTicks}
            tick={props => {
              const { x, y, fill, payload, index } = props;
              const offsetX = index === 1 ? 10 : 0;
              const isSingleTick = xAxisTicks.length === 1;

              return (
                <g className="recharts-layer recharts-cartesian-axis-tick">
                  <text
                    className="recharts-text recharts-cartesian-axis-tick-value"
                    x={x}
                    y={y}
                    fill={fill}
                    textAnchor={isSingleTick ? "middle" : "start"}
                  >
                    <tspan x={x - offsetX} y={y}>
                      {moment(payload.value).format("M/D/YY")}
                    </tspan>
                  </text>
                </g>
              );
            }}
            padding={{ left: 0, right: 15 }}
          />
          <YAxis
            dataKey="amount"
            type="number"
            domain={[0, maxOffer]}
            ticks={[0, maxOffer]}
            tickFormatter={value => [
              formatCurrencyAmount("USD", value, undefined, {
                precision: 3,
              }),
            ]}
            tickLine={false}
            tickMargin={0}
            tick={({ x, y, fill, payload }) => (
              <g className="recharts-layer recharts-cartesian-axis-tick">
                <text
                  className="recharts-text recharts-cartesian-axis-tick-value"
                  x={x}
                  y={y}
                  fill={fill}
                  textAnchor="end"
                >
                  <tspan x={x} y={y}>
                    {formatCurrencyAmount("USD", payload.value, undefined, {
                      precision: 3,
                    })}
                  </tspan>
                </text>
              </g>
            )}
            padding={{ top: 10, bottom: 0 }}
            width={99}
          />
          {map(buyNowPriceHistorySegments, (buyNowPriceHistorySegment, i) => {
            return (
              <ReferenceLine
                key={i}
                segment={buyNowPriceHistorySegment}
                stroke="black"
              />
            );
          })}
          {map(chartOffersSorted, (scatter_data, key) => (
            <Scatter
              key={key}
              name={key}
              data={scatter_data.data}
              fill={scatter_data.color}
            />
          ))}
          {canSeeMarketplaceMedian &&
          !!marketplaceMedian &&
          !isAcceptingFinalOffers ? (
            <ReferenceLine
              y={marketplaceMedian}
              stroke="grey"
              strokeDasharray="3 3"
              label={({ viewBox }) => (
                <text
                  x={viewBox.x + 15}
                  y={viewBox.y - 5}
                  fill="gray"
                  className={classes.referenceLineLabel}
                >
                  <tspan>
                    {`Current Median Price: ${formatCurrencyAmount(
                      "USD",
                      marketplaceMedian,
                    )}`}
                  </tspan>
                </text>
              )}
            />
          ) : null}
          {isAcceptingFinalOffers && !!acceptingFinalOffersStartAt && (
            <ReferenceLine
              x={new Date(acceptingFinalOffersStartAt).valueOf()}
              stroke="gray"
              strokeDasharray="3 3"
              label={({ viewBox }) => {
                const { x, y, width, height } = viewBox;
                const lineX = x + width / 2;
                return (
                  <text
                    x={lineX + 10}
                    y={y + height / 2 - 15}
                    fill="gray"
                    textAnchor="middle"
                    transform={`rotate(-90, ${lineX + 10}, ${y + height / 2})`}
                    className={classes.referenceLineLabel}
                  >
                    <tspan>Accepting Final Offers</tspan>
                  </text>
                );
              }}
            />
          )}
          <Tooltip content={<OfferChartCustomTooltip currency="USD" />} />
        </ScatterChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default OfferChart;
